<template>
  <div
    ref="headerCellRef"
    :class="[
      'sf-virtual-table__header-cell',
      'tw-flex',
      'tw-items-center',
      'tw-relative',
      {
        'sf-virtual-table__header-cell--sticky': isSticky,
        'sf-virtual-table__header-cell--sticky-last': isLastStickyColumn,
      },
    ]"
    :style="stickyStyle"
    data-testid="sf-virtual-table-header-cell"
  >
    <slot />
    <div
      v-if="showResizeHandle"
      class="
        sf-virtual-table__resize-handle
        tw-bg-transparent
        hover:tw-bg-sf-slate-lighter-2
      "
      @mousedown="startResize"
    ></div>
  </div>
</template>

<script setup>
import { computed, inject, onBeforeUnmount, ref } from 'vue'
import { useStickyColumn } from '@app/shared/vue/composables/use-sticky-column'

const resizable = inject('resizable', false)
const rowSize = inject('rowSize', 0)

const registerHeaderCell = inject('registerHeaderCell', () => 0)
const resizeColumn = inject('resizeColumn', () => {})
const setResizingColumn = inject('setResizingColumn', () => {})
const captureColumnWidths = inject('captureColumnWidths', () => {})
const prepareColumnResize = inject('prepareColumnResize', () => {})

const headerCellRef = ref(null)
const columnIndex = ref(0)
const isResizing = ref(false)
const startX = ref(0)
const startWidth = ref(0)

// Register this header cell on mount
columnIndex.value = registerHeaderCell()

const isLastColumn = computed(() => columnIndex.value === rowSize.value - 1)

const showResizeHandle = computed(() => resizable.value && !isLastColumn.value)

// Sticky column styling
const { isSticky, isLastStickyColumn, stickyStyle } = useStickyColumn({
  columnIndex,
})

const startResize = (event) => {
  event.preventDefault()
  event.stopPropagation()

  isResizing.value = true
  startX.value = event.pageX

  startWidth.value = headerCellRef.value.offsetWidth

  prepareColumnResize(columnIndex.value, startWidth.value)

  // Notify parent that we're resizing (switches to fixed widths)
  setResizingColumn(true, columnIndex.value)

  document.addEventListener('mousemove', handleResize)
  document.addEventListener('mouseup', stopResize)
  document.body.style.cursor = 'col-resize'
}

const handleResize = (event) => {
  if (!isResizing.value) return

  const diff = event.pageX - startX.value
  const newWidth = startWidth.value + diff

  resizeColumn(columnIndex.value, newWidth)
}

const stopResize = () => {
  isResizing.value = false
  removeResizeListeners()
  document.body.style.cursor = ''

  // Capture all current column widths before switching back to minmax
  // This preserves the current distribution of column sizes
  captureColumnWidths()

  // Notify parent that we're done resizing (switches back to minmax)
  setResizingColumn(false)
}

const removeResizeListeners = () => {
  document.removeEventListener('mousemove', handleResize)
  document.removeEventListener('mouseup', stopResize)
}

onBeforeUnmount(() => {
  removeResizeListeners()
})
</script>

<style lang="scss" scoped>
@import 'silverfin-ui-kit/src/scss/tooling';

.sf-virtual-table__header-cell {
  @include table-header-cell;

  border-top-style: none;
  border-left-style: none;
  border-bottom-style: none;
  overflow: visible;

  &:last-child {
    border-right: 0;
  }

  &--sticky {
    background-color: var(--sf-virtual-table-header-bg, white);
    will-change: transform;
    backface-visibility: hidden;
  }

  // Outrank the sticky data cells (inline z-index up to 11) that otherwise
  // paint over an open menu; !important beats this cell's own inline z-index.
  &:has(.sf-dropdown2-menu--open) {
    z-index: 12 !important;
  }

  &--sticky-last {
    // Use !important to override table-header-cell mixin border styles
    border-right: 1px solid theme('colors.sf-slate.subtle-2') !important;
  }
}

.sf-virtual-table__resize-handle {
  position: absolute;
  right: -7px;
  top: 0;
  bottom: 0;
  width: 14px;
  cursor: col-resize;
  user-select: none;
  z-index: 10;
  pointer-events: auto;
}
</style>
